import {
  ContextQuestion,
  ObjectiveQuestion,
  DecisionTreeQuestion,
  CaseStudyQuestion,
} from 'pages/new-questions/types';
import { UserStepAnswer } from 'pages/new-questions/components/DecisionTreeQuestion/utils/types';

export type LectureQuestion = (
  | ContextQuestion
  | ObjectiveQuestion
  | DecisionTreeQuestion
  | CaseStudyQuestion
) & {
  /**
   * > 0 quando a aula vincula UMA etapa individual do Estudo de Caso (`step_multiple` vem
   * filtrado nela). Ausente no vínculo legado do caso inteiro e nas questões tradicionais.
   */
  question_step_id?: number;
  /** Código da etapa individual vinculada (espelha `step_multiple[0].code`). */
  step_code?: string | null;
  pivot?: {
    id_aula: number;
    id_questao: number;
    ordem: number;
    question_step_id?: number;
    block_id?: number | null;
  };
};

export const IDK_ANSWER_ID = 0;

export interface LectureAnswersState {
  objective: Record<number, number>;
  context: Record<number, string>;
  decisionTree: Record<number, UserStepAnswer[]>;
  /** case_study: questionId → stepId → answerId */
  caseStudy: Record<number, Record<number, number>>;
  questionTime: Record<number, string>;
  contador: string;
}

export interface LectureData {
  id_aula: number;
  class_type_id: number | null;
  cod_aula: string;
  titulo: string;
  thumbnail: string | null;
  pdf_file: string | null;
  class_time: string | null;
  weight: number | null;
  audio_embed: string | null;
  video_embed: string | null;
  video_embed_libras: string | null;
  texto: string | null;
  minimo_aprovacao: number;
  video_embed_tmp: string | null;
  peso: number | null;
  class_type: {
    slug: string;
    id: number;
    [key: string]: any;
  } | null;
  questions: LectureQuestion[];
  total_questions: number;
  deleted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  question_blocks_enabled?: boolean;
  question_blocks?: QuestionBlockSummary[];
}

export type QuestionBlockStatus = 'not_started' | 'in_progress' | 'completed';

export interface QuestionBlockLastExam {
  id_prova: number;
  score_percentage: number;
  data_prova: string;
}

export interface QuestionBlockSummary {
  id: number;
  title: string;
  display_order: number;
  total_questions: number;
  answered_questions: number;
  status: QuestionBlockStatus;
  exams_count: number;
  last_exam: QuestionBlockLastExam | null;
}

/** Resposta de GET /v2/students/classes/{id}/blocks/{blockId} */
export interface LectureBlockQuestionsResponse {
  questions: LectureQuestion[];
}

export const isQuestionBlocksMode = (
  lecture: Pick<LectureData, 'question_blocks_enabled' | 'question_blocks'> | null | undefined,
): boolean =>
  Boolean(lecture?.question_blocks_enabled) && (lecture?.question_blocks?.length ?? 0) > 0;

export const isObjectiveQuestion = (question: LectureQuestion): question is ObjectiveQuestion => {
  return question.question_type === 'objective_questions';
};

export const isContextQuestion = (question: LectureQuestion): question is ContextQuestion => {
  return question.question_type === 'question_context';
};

export const isDecisionTreeQuestion = (
  question: LectureQuestion
): question is DecisionTreeQuestion => {
  return question.question_type === 'decision_tree';
};

export const isCaseStudyQuestion = (
  question: LectureQuestion
): question is CaseStudyQuestion => {
  return question.question_type === 'case_study';
};

export const isQuestionAnswered = (
  question: LectureQuestion,
  answers: LectureAnswersState
): boolean => {
  if (isObjectiveQuestion(question)) {
    return answers.objective[question.id_questao] !== undefined;
  }

  if (isContextQuestion(question)) {
    return (
      answers.context[question.id_questao] !== undefined &&
      answers.context[question.id_questao].trim().length > 0
    );
  }

  if (isDecisionTreeQuestion(question)) {
    const userAnswers = answers.decisionTree[question.id_questao];
    if (!userAnswers || userAnswers.length === 0) return false;

    const totalSingleSteps = question.step_single?.length || 0;
    if (totalSingleSteps > 0) {
      const answeredSingleStepIndexes = new Set(
        userAnswers.filter((a) => a.stepType === 'single').map((a) => a.stepIndex),
      );
      return answeredSingleStepIndexes.size >= totalSingleSteps;
    }

    return userAnswers.length > 0;
  }

  if (isCaseStudyQuestion(question)) {
    // O balde é compartilhado pelas N entradas do mesmo Estudo de Caso, então contar o
    // tamanho dele marcaria todas as entradas como respondidas assim que UMA etapa fosse
    // respondida. Uma entrada está respondida quando TODAS as etapas QUE ELA COBRE têm
    // resposta: só a própria etapa no vínculo por etapa, todas no vínculo legado.
    const stepAnswers = answers.caseStudy[question.id_questao] || {};

    return (question.step_multiple ?? []).every((step) => stepAnswers[step.id] !== undefined);
  }

  return false;
};

export interface ReviewExamInfo {
  id_prova: number;
  course: string;
  lesson_id: number;
  date: string;
  time_spent: string;
}

export interface ReviewObjectiveQuestion {
  question_type: 'objective_questions';
  question_id: number;
  question_step_id?: number | null;
  answer_id: number;
  order: number | null;
  is_correct: boolean;
  correct_answer_id: number;
}

export interface ReviewContextQuestion {
  question_type: 'question_context';
  question_id: number;
  question_step_id: number | null;
  context_answers: string;
  time_spent: string;
  is_correct: boolean;
  evaluation?: {
    nota: number;
    veredicto: string;
    justificativa: string;
  };
}

export interface ReviewDecisionTreeQuestion {
  question_type: 'decision_tree';
  question_id: number;
  question_step_id: number;
  answer_id: number;
  time_spent: string;
  is_correct: boolean;
  correct_answer_id: number;
}

export interface ReviewCaseStudyQuestion {
  question_type: 'case_study';
  question_id: number;
  question_step_id: number;
  answer_id: number;
  time_spent: string;
  is_correct: boolean;
  correct_answer_id: number;
}

export type ReviewQuestion =
  | ReviewObjectiveQuestion
  | ReviewContextQuestion
  | ReviewDecisionTreeQuestion
  | ReviewCaseStudyQuestion;

export interface ReviewResponse {
  exam_info: ReviewExamInfo;
  questions: ReviewQuestion[];
}
